import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { S, formatDate } from '../utils/styles';
import { useAuth } from '../App';

export default function Production() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [stages, setStages] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [newStageName, setNewStageName] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/orders?status=active').then(r => setOrders(r.data)).catch(()=>{});
    api.get('/inventory/job-vendors').then(r => setVendors(r.data)).catch(()=>{});
    api.get('/stage-templates').then(r => setTemplates(r.data)).catch(()=>{});
  }, []);

  const loadOrderItems = async (orderId) => {
    const { data } = await api.get(`/orders/${orderId}`);
    setSelectedOrder(data);
    const realItems = data.items.filter(i => i.size);
    if (realItems.length > 0) selectItem(orderId, realItems[0]);
  };

  const [suggestion, setSuggestion] = useState(null);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [pickedTemplateId, setPickedTemplateId] = useState('');
  const [applyingPicked, setApplyingPicked] = useState(false);

  const selectItem = async (orderId, item) => {
    setSelectedItem(item);
    const { data } = await api.get(`/orders/${orderId}/items/${item.id}/stages`);
    setStages(data);
    setSuggestion(null);
    try {
      const { data: sugg } = await api.get(`/orders/${orderId}/items/${item.id}/stage-suggestions`);
      if (sugg.source !== 'none' && sugg.stages.length > 0) setSuggestion(sugg);
    } catch (e) { /* suggestion is a nice-to-have, never block the page on it */ }
  };

  const [dragOverId, setDragOverId] = useState(null);

  const handleDragStart = (e, stageId) => { e.dataTransfer.setData('stageId', String(stageId)); };
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    const draggedId = parseInt(e.dataTransfer.getData('stageId'));
    if (draggedId === targetId) return;
    setDragOverId(null);
    const reordered = [...stages];
    const fromIdx = reordered.findIndex(s => s.id === draggedId);
    const toIdx = reordered.findIndex(s => s.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const withOrder = reordered.map((s, i) => ({ ...s, col_order: i }));
    setStages(withOrder);
    try {
      await api.post(`/orders/stages/reorder`, { stages: withOrder.map(s => ({ id: s.id, col_order: s.col_order })) });
    } catch(e) {}
  };

  const addStage = async () => {
    if (!newStageName.trim() || !selectedItem || !selectedOrder) return;
    await api.post(`/orders/${selectedOrder.order.id}/items/${selectedItem.id}/stages`, {
      stage_name: newStageName, col_order: stages.length
    });
    setNewStageName('');
    const { data } = await api.get(`/orders/${selectedOrder.order.id}/items/${selectedItem.id}/stages`);
    setStages(data);
  };

  const applySuggestion = async () => {
    if (!suggestion || !selectedItem || !selectedOrder) return;
    setApplyingSuggestion(true);
    try {
      const { data } = await api.post(`/orders/${selectedOrder.order.id}/items/${selectedItem.id}/stages/bulk`, {
        stage_names: suggestion.stages,
      });
      setStages(data);
      setSuggestion(null);
    } catch (e) {
      alert('Error applying suggested stages');
    } finally {
      setApplyingSuggestion(false);
    }
  };

  const applyPickedTemplate = async () => {
    if (!pickedTemplateId || !selectedItem || !selectedOrder) return;
    const tpl = templates.find(t => String(t.id) === String(pickedTemplateId));
    if (!tpl) return;
    setApplyingPicked(true);
    try {
      const { data } = await api.post(`/orders/${selectedOrder.order.id}/items/${selectedItem.id}/stages/bulk`, {
        stage_names: tpl.stages,
      });
      setStages(data);
      setPickedTemplateId('');
      setSuggestion(null);
    } catch (e) {
      alert('Error applying template');
    } finally {
      setApplyingPicked(false);
    }
  };

  const [savingTemplate, setSavingTemplate] = useState(false);
  const saveAsTemplate = async () => {
    if (stages.length === 0) return alert('Add some stages first before saving them as a template');
    const name = window.prompt('Name this template (e.g. "RL 20-25 — Export finish"):');
    if (!name || !name.trim()) return;
    setSavingTemplate(true);
    try {
      await api.post('/stage-templates', { name: name.trim(), stages: stages.map(s => s.stage_name) });
      const { data } = await api.get('/stage-templates');
      setTemplates(data);
      alert(`Saved "${name.trim()}" — it'll show up in "Apply a process" for any item now.`);
    } catch (e) {
      alert(e.response?.data?.error || 'Error saving template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const updateStage = async (stageId, field, value) => {
    const stage = stages.find(s => s.id === stageId);
    if (!stage) return;
    await api.put(`/orders/stages/${stageId}`, { ...stage, [field]: value });
    setStages(ss => ss.map(s => s.id === stageId ? {...s, [field]: value} : s));
  };

  const deleteStage = async id => {
    if (!window.confirm('Remove this stage?')) return;
    await api.delete(`/orders/stages/${id}`);
    setStages(ss => ss.filter(s => s.id !== id));
  };

  const renameStage = async (id, name) => {
    const newName = window.prompt('Rename stage:', name);
    if (!newName || newName === name) return;
    await api.put(`/orders/stages/${id}/rename`, { stage_name: newName });
    setStages(ss => ss.map(s => s.id === id ? {...s, stage_name: newName} : s));
  };

  const QUICK_STAGES = ['Cut nut','Cut bottom','Bend nut','Bend bottom','Spot weld','Tap','EG plating','Rubber assy','Screw assy','Boxing'];
  const availableQuickStages = QUICK_STAGES.filter(s => !stages.some(st => st.stage_name === s));

  const filteredOrders = orders.filter(o =>
    !search || o.pi_number.toLowerCase().includes(search.toLowerCase()) ||
    o.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalOrdered = selectedItem?.qty_ordered || 0;

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:22, fontWeight:600, margin:0 }}>Production Tracker</h1>
        <div style={{ fontSize:13, color:'#9ca3af', marginTop:3 }}>Track manufacturing stages for each line item across active orders</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:18 }}>
        {/* Left: order + item selector */}
        <div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search orders..." style={{ ...S.input, marginBottom:12 }} />
          <div style={{ fontSize:11, fontWeight:600, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.7, marginBottom:8 }}>Active orders</div>
          {filteredOrders.length === 0 && <div style={{ color:'#9ca3af', fontSize:13 }}>No active orders</div>}
          {filteredOrders.map(o => (
            <div key={o.id}
              onClick={() => loadOrderItems(o.id)}
              style={{ padding:'9px 11px', borderRadius:7, cursor:'pointer', marginBottom:4,
                background: selectedOrder?.order?.id===o.id ? '#eff6ff' : '#f9fafb',
                border: selectedOrder?.order?.id===o.id ? '1px solid #bfdbfe' : '1px solid #e5e7eb',
                color: selectedOrder?.order?.id===o.id ? '#3b82f6' : '#374151' }}>
              <div style={{ fontWeight:500, fontSize:13 }}>{o.pi_number}</div>
              <div style={{ fontSize:11, color:'#9ca3af' }}>{o.company_name} · {formatDate(o.expected_ship_date)}</div>
            </div>
          ))}
        </div>

        {/* Right: stage grid */}
        <div>
          {!selectedOrder && (
            <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:40, textAlign:'center', color:'#9ca3af' }}>
              Select an order on the left to view and update production stages
            </div>
          )}

          {selectedOrder && (
            <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:600 }}>{selectedOrder.order.pi_number} — {selectedOrder.order.company_name}</div>
                  <div style={{ fontSize:12, color:'#9ca3af', marginTop:2 }}>Ship: {formatDate(selectedOrder.order.expected_ship_date)}</div>
                </div>
                {user?.role === 'owner' && (
                  <Link to={`/orders/${selectedOrder.order.id}`} style={{ fontSize:12, color:'#3b82f6', textDecoration:'none' }}>View full order →</Link>
                )}
              </div>

              {/* Line item selector */}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
                {selectedOrder.items.filter(i => i.size).map(item => (
                  <div key={item.id}
                    onClick={() => selectItem(selectedOrder.order.id, item)}
                    style={{ padding:'6px 12px', borderRadius:20, cursor:'pointer', fontSize:12,
                      background: selectedItem?.id===item.id ? '#3b82f6' : '#f3f4f6',
                      color: selectedItem?.id===item.id ? '#fff' : '#374151',
                      border: selectedItem?.id===item.id ? '1px solid #3b82f6' : '1px solid #e5e7eb' }}>
                    {item.product_code} {item.size} ({(item.qty_ordered||0).toLocaleString()})
                  </div>
                ))}
              </div>

              {selectedItem && (
                <>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>
                    {selectedItem.product_code} · {selectedItem.size} · {totalOrdered.toLocaleString()} pcs ordered
                  </div>

                  {/* Horizontal stage grid — fix #12: proper scroll */}
                  <div style={{ overflowX:'auto', marginBottom:16, border:'1px solid #e5e7eb', borderRadius:8, maxWidth:'100%' }}>
                    <table style={{ borderCollapse:'collapse', minWidth: Math.max(600, stages.length * 160 + 200) }}>
                      <thead>
                        <tr style={{ background:'#f9fafb' }}>
                          <th style={{ ...S.th, minWidth:120, position:'sticky', left:0, background:'#f9fafb', zIndex:2 }}>Field</th>
                          {stages.map(s => (
                            <th key={s.id} style={{ ...S.th, minWidth:150, textAlign:'center', cursor:'grab',
                              background: dragOverId===s.id ? '#dbeafe' : '#f9fafb',
                              borderLeft: dragOverId===s.id ? '2px solid #3b82f6' : 'none' }}
                              draggable
                              onDragStart={e => handleDragStart(e, s.id)}
                              onDragOver={e => { e.preventDefault(); setDragOverId(s.id); }}
                              onDragLeave={() => setDragOverId(null)}
                              onDrop={e => handleDrop(e, s.id)}
                            >
                              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                                <span>{s.stage_name}</span>
                                <button onClick={() => renameStage(s.id, s.stage_name)} style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:11, padding:0 }}>✎</button>
                                <button onClick={() => deleteStage(s.id)} style={{ background:'none', border:'none', color:'#fca5a5', cursor:'pointer', fontSize:13, padding:0 }}>×</button>
                              </div>
                              {s.updated_at && <div style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>{s.updated_at?.split('T')[0]}</div>}
                            </th>
                          ))}
                          {stages.length === 0 && <th style={{ ...S.th, color:'#9ca3af', fontStyle:'italic' }}>Add stages below →</th>}
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom:'1px solid #f3f4f6' }}>
                          <td style={{ ...S.td, fontWeight:500, background:'#fafafa', position:'sticky', left:0, zIndex:1 }}>Done (pcs)</td>
                          {stages.map(s => {
                            const pct = totalOrdered > 0 ? Math.round((s.qty_done/totalOrdered)*100) : 0;
                            return (
                              <td key={s.id} style={{ padding:'8px 10px', textAlign:'center' }}>
                                <input type="number" value={s.qty_done} min={0} max={totalOrdered}
                                  onChange={e => updateStage(s.id,'qty_done',parseInt(e.target.value)||0)}
                                  style={{ ...S.input, width:80, textAlign:'center', fontSize:13 }} />
                                <div style={{ marginTop:4 }}>
                                  <div style={{ width:80, height:4, background:'#e5e7eb', borderRadius:2, overflow:'hidden', margin:'0 auto' }}>
                                    <div style={{ width:`${pct}%`, height:'100%', background:pct>=100?'#10b981':'#3b82f6' }} />
                                  </div>
                                  <div style={{ fontSize:10, color:'#9ca3af', marginTop:2 }}>{pct}%</div>
                                </div>
                              </td>
                            );
                          })}
                          {stages.length === 0 && <td style={{ ...S.td, color:'#9ca3af', textAlign:'center' }}>—</td>}
                        </tr>
                        <tr style={{ borderBottom:'1px solid #f3f4f6' }}>
                          <td style={{ ...S.td, fontWeight:500, background:'#fafafa', position:'sticky', left:0, zIndex:1 }}>With vendor</td>
                          {stages.map(s => (
                            <td key={s.id} style={{ padding:'8px 10px', textAlign:'center' }}>
                              <input type="number" value={s.qty_with_vendor} min={0}
                                onChange={e => updateStage(s.id,'qty_with_vendor',parseInt(e.target.value)||0)}
                                style={{ ...S.input, width:80, textAlign:'center', fontSize:13 }} />
                            </td>
                          ))}
                          {stages.length === 0 && <td style={{ ...S.td, color:'#9ca3af', textAlign:'center' }}>—</td>}
                        </tr>
                        <tr>
                          <td style={{ ...S.td, fontWeight:500, background:'#fafafa', position:'sticky', left:0, zIndex:1 }}>Vendor</td>
                          {stages.map(s => (
                            <td key={s.id} style={{ padding:'8px 10px', textAlign:'center' }}>
                              <select value={s.vendor_id||''} onChange={e => updateStage(s.id,'vendor_id',e.target.value||null)}
                                style={{ ...S.input, fontSize:12, width:120 }}>
                                <option value="">None</option>
                                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                              </select>
                              {vendors.length === 0 && <div style={{ fontSize:10, color:'#9ca3af', marginTop:3 }}>
                                <a href="/vendors" style={{ color:'#3b82f6', textDecoration:'none' }}>+ Add vendors →</a>
                              </div>}
                            </td>
                          ))}
                          {stages.length === 0 && <td style={{ ...S.td, color:'#9ca3af', textAlign:'center' }}>—</td>}
                        </tr>
                        <tr style={{ background:'#f9fafb' }}>
                          <td style={{ ...S.td, fontWeight:400, fontSize:11, color:'#9ca3af', background:'#f9fafb', position:'sticky', left:0, zIndex:1 }}>Last updated</td>
                          {stages.map(s => {
                            const d = s.updated_at ? new Date(s.updated_at) : null;
                            const daysAgo = d ? Math.floor((Date.now()-d.getTime())/(1000*60*60*24)) : null;
                            const label = daysAgo === null ? '—' : daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`;
                            return <td key={s.id} style={{ padding:'6px 10px', textAlign:'center', fontSize:11, color:'#9ca3af' }}>{label}</td>;
                          })}
                          {stages.length === 0 && <td style={{ ...S.td, color:'#9ca3af', textAlign:'center' }}>—</td>}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {suggestion && (
                    <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                      <div style={{ fontSize:12, color:'#1e40af', marginBottom:6 }}>
                        {suggestion.source === 'reorder'
                          ? `Suggested from a previous order of this exact item (${suggestion.from.pi_number}, ${formatDate(suggestion.from.order_date)}):`
                          : `Suggested from the "${suggestion.from.template_name}" production template:`}
                      </div>
                      <div style={{ fontSize:12, color:'#374151', marginBottom:8 }}>{suggestion.stages.join(' → ')}</div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={applySuggestion} disabled={applyingSuggestion} style={{ ...S.btnSm, background:'#3b82f6', color:'#fff' }}>
                          {applyingSuggestion ? 'Applying...' : `+ Add all ${suggestion.stages.length} stages`}
                        </button>
                        <button onClick={() => setSuggestion(null)} style={{ ...S.btnSm, background:'none', color:'#6b7280' }}>Dismiss</button>
                      </div>
                    </div>
                  )}

                  {templates.length > 0 && (
                    <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:12 }}>
                      <span style={{ fontSize:12, color:'#6b7280' }}>Apply a process:</span>
                      <select value={pickedTemplateId} onChange={e => setPickedTemplateId(e.target.value)} style={{ ...S.input, width:220, fontSize:12, padding:'4px 8px' }}>
                        <option value="">Choose a template...</option>
                        {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      <button onClick={applyPickedTemplate} disabled={!pickedTemplateId || applyingPicked} style={{ ...S.btnSm, background:'#f3f4f6', color:'#374151' }}>
                        {applyingPicked ? 'Applying...' : '+ Apply'}
                      </button>
                    </div>
                  )}

                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <input value={newStageName} onChange={e => setNewStageName(e.target.value)}
                      onKeyDown={e => e.key==='Enter'&&addStage()}
                      placeholder="Stage name..." style={{ ...S.input, width:220 }} />
                    <button onClick={addStage} disabled={!newStageName.trim()} style={S.btnSecondary}>+ Add stage</button>
                    {availableQuickStages.length > 0 && <span style={{ fontSize:12, color:'#9ca3af' }}>Quick:</span>}
                    {availableQuickStages.map(s => (
                      <button key={s} onClick={() => setNewStageName(s)}
                        style={{ ...S.btnSm, background:'#f3f4f6', color:'#374151', whiteSpace:'nowrap', fontSize:11 }}>{s}</button>
                    ))}
                  </div>

                  {user?.role === 'owner' && stages.length > 0 && (
                    <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #f3f4f6' }}>
                      <button onClick={saveAsTemplate} disabled={savingTemplate} style={{ ...S.btnSm, background:'#f3f4f6', color:'#374151' }}>
                        {savingTemplate ? 'Saving...' : '💾 Save these stages as a template'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
